import Title from "../ui/title/Title";
import { Row, SectionContainer } from "../ui/grid";
import styles from './Payment.module.scss';
import { NavLink, useParams, useSearchParams } from "react-router-dom";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getParkingSessionCost, ParkingSessionCostIntervalType } from "api/query/ParkingSession";
import { calculateSubscription, CalculateSubscriptionResult, getSubscription, SubscriptionItemResp } from "api/query/Subscription";
import Layout from '../layout'
import PaymentCheckSvg from "../../svg/PaymentCheckSvg";
import EndCheckSvg from "../../svg/EndCheckSvg";
import { PaymentInfoTypeResp } from "../../api/query/Payment";
import IcInfoSvg from "../../svg/IcInfoSvg";
import { LoaderCircle } from "./loader/LoaderCircle";
import { InfoType } from "../../api/query/Info";

const PaymentPage = ({ paymentType }: { paymentType: PaymentInfoTypeResp }) => {
  const { zone, sessionID } = useParams();
  const [searchParams] = useSearchParams();
  const grzParam = searchParams.get("grz");
  const phoneParamRaw = searchParams.get("phone");
  const phoneParam = phoneParamRaw && phoneParamRaw !== 'Не указан' ? phoneParamRaw : undefined;
  const quantityParamRaw = Number(searchParams.get("quantity") || 1);
  const quantityParam = Number.isFinite(quantityParamRaw) ? Math.max(1, quantityParamRaw) : 1;
  const promoCodeParam = searchParams.get("promoCode") || '';
  const amountParamRaw = Number(searchParams.get("amount") || -1);
  const amountParam = Number.isFinite(amountParamRaw) ? amountParamRaw : -1;
  const timestartParam = searchParams.get("timestart") || '';
  const timeendParam = searchParams.get("timeend") || '';
  const subscriptionGrzList = (searchParams.get("subscriptionGrz") || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 10);

  const [subscriptionV2EnabledState, setSubscriptionV2EnabledState] = useState<boolean>(false);
  const [doPay, setDoPay] = useState<boolean>(true);
  const [costState, setCostState] = useState<number>(amountParam);
  const [timeEndState, setTimeEndState] = useState<string>('--:--');
  const [timeStartState, setTimeStartState] = useState<string>('--:--');
  const [diffMinutesState, setDiffMinutesState] = useState<number>(-1);
  const [costReasonState, setCostReasonState] = useState<string>('');
  const [specialMessageState, setSpecialMessageState] = useState<string>('');
  const [timestartRawState, setTimestartRawState] = useState<string>('');
  const [timeendRawState, setTimeendRawState] = useState<string>('');
  const [customTimestartState, setCustomTimestartState] = useState<string>(timestartParam);
  const [fixTimestartState, setFixTimestartState] = useState<boolean>(false);
  const [fixTimeendState, setFixTimeendState] = useState<boolean>(false);
  const [durationMsState, setDurationMsState] = useState<number>(0);
  const [intervalsState, setIntervalsState] = useState<ParkingSessionCostIntervalType[]>([]);
  const [subscriptionLoadingState, setSubscriptionLoadingState] = useState<boolean>(paymentType === PaymentInfoTypeResp.abonnement);
  const [subscriptionItemState, setSubscriptionItemState] = useState<SubscriptionItemResp | null>(null);
  const [quantityState, setQuantityState] = useState<number>(quantityParam);
  const [promoInputState, setPromoInputState] = useState<string>(promoCodeParam);
  const [appliedPromoCodeState, setAppliedPromoCodeState] = useState<string>(promoCodeParam);
  const [calculationState, setCalculationState] = useState<CalculateSubscriptionResult | null>(null);
  const [calculationLoadingState, setCalculationLoadingState] = useState<boolean>(false);
  const [calculationErrorState, setCalculationErrorState] = useState<string>('');
  const [detailsHighlightState, setDetailsHighlightState] = useState<boolean>(false);
  const detailsRef = useRef<HTMLDivElement>(null);
  const isSubscriptionPromoEnabled = subscriptionV2EnabledState;

  const handleInfoLoaded = useCallback((info: InfoType) => {
    setSubscriptionV2EnabledState(!!info.result.subscriptionV2Enabled);
  }, []);

  useEffect(() => {
    setQuantityState(quantityParam);
  }, [quantityParam]);

  useEffect(() => {
    setPromoInputState(promoCodeParam);
    setAppliedPromoCodeState(promoCodeParam);
  }, [promoCodeParam]);

  useEffect(() => {
    if (!sessionID) return;
    if (paymentType === PaymentInfoTypeResp.abonnement) {
      if (grzParam) {
        setSubscriptionLoadingState(true);
        getSubscription(zone, grzParam, phoneParam || undefined)
          .then((data) => {
            const selected = (data.result || []).find((item: SubscriptionItemResp) => sessionID === item.code);
            if (selected) {
              setSubscriptionItemState(selected);
              setCostState(isSubscriptionPromoEnabled ? amountParam : selected.price);
              const selectedTimestart = timestartParam || selected.timestart;
              const selectedTimeend = timeendParam || selected.timeend;
              setTimeEndState(formatDateString(selectedTimeend));
              setTimeStartState(formatDateString(selectedTimestart));
              setTimestartRawState(selectedTimestart);
              setTimeendRawState(selectedTimeend);
              setCustomTimestartState(selectedTimestart);
              setFixTimestartState(!!selected.fixtimestart);
              setFixTimeendState(!!selected.fixtimeend);
              const startMs = Date.parse(selected.timestart);
              const endMs = Date.parse(selected.timeend);
              if (!isNaN(startMs) && !isNaN(endMs)) {
                setDurationMsState(endMs - startMs);
              }
            }
            if(data.result) setDoPay(false)
          })
          .finally(() => {
            setSubscriptionLoadingState(false);
          })
      } else {
        setSubscriptionLoadingState(false);
      }
    } else {
      getParkingSessionCost(zone, sessionID)
        .then((data) => {
          if(data.result) setDoPay(false)
          setCostState(data.result.cost);
          setIntervalsState(Array.isArray(data.result.intervals) ? data.result.intervals : []);
          setSpecialMessageState(data.result.specialmessage?.text || '');
          const timeIn = typeof data.result.timein === 'string' ? data.result.timein.split('T')[1] : undefined;
          const timeOut = typeof data.result.timeout === 'string' ? data.result.timeout.split('T')[1] : undefined;
          if (timeIn) setTimeStartState(timeIn.substring(0, timeIn.length - 3));
          if (timeOut) setTimeEndState(timeOut.substring(0, timeOut.length - 3));
          const diff = Date.parse(data.result.timeout || '') - Date.parse(data.result.timein || '');
          if (!isNaN(diff)) setDiffMinutesState(Math.floor(diff / 60000));
          if(data.result.cost === 0 && data.result.reason ) setCostReasonState(data.result.reason)
        })
    }
  }, [zone, sessionID, paymentType, grzParam, phoneParam, isSubscriptionPromoEnabled, amountParam, timestartParam, timeendParam])

  const isFixedSubscriptionDates = fixTimestartState && fixTimeendState;
  const requestTimestart = isFixedSubscriptionDates
    ? timestartRawState
    : (customTimestartState || timestartRawState || '');

  useEffect(() => {
    if (!isSubscriptionPromoEnabled || paymentType !== PaymentInfoTypeResp.abonnement || !sessionID || !grzParam || !subscriptionItemState || !requestTimestart) {
      setCalculationLoadingState(false);
      return;
    }

    let isCancelled = false;
    setCalculationLoadingState(true);
    setCalculationErrorState('');

    const timer = window.setTimeout(() => {
      calculateSubscription(zone, {
        grz: grzParam,
        phone: phoneParam ?? 'Не указан',
        promoCode: appliedPromoCodeState,
        permissionKindCode: sessionID,
        quantity: quantityState,
        timestart: requestTimestart,
      })
        .then((data) => {
          if (isCancelled) return;
          const result = data.result;
          if (!result || !result.ok || typeof result.amount !== 'number') {
            setCalculationState(null);
            setCalculationErrorState('Не удалось рассчитать стоимость абонемента.');
            return;
          }
          setCalculationState(result);
          setCostState(result.amount);
          if (result.timestart) {
            setTimeStartState(formatDateString(result.timestart));
            setTimestartRawState(result.timestart);
          }
          if (result.timeend) {
            setTimeEndState(formatDateString(result.timeend));
            setTimeendRawState(result.timeend);
          }
        })
        .catch(() => {
          if (isCancelled) return;
          setCalculationState(null);
          setCalculationErrorState('Не удалось рассчитать стоимость абонемента.');
        })
        .finally(() => {
          if (!isCancelled) setCalculationLoadingState(false);
        });
    }, 450);

    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
    };
  }, [zone, sessionID, paymentType, grzParam, phoneParam, isSubscriptionPromoEnabled, subscriptionItemState, quantityState, appliedPromoCodeState, requestTimestart])

  let link = '';
  const subscriptionCalculationRequired = isSubscriptionPromoEnabled && (quantityState !== 1 || appliedPromoCodeState !== '');
  const subscriptionCalculationInProgress = isSubscriptionPromoEnabled
    && paymentType === PaymentInfoTypeResp.abonnement
    && (calculationLoadingState || subscriptionLoadingState);
  const subscriptionCalculationFailed = paymentType === PaymentInfoTypeResp.abonnement && !!calculationErrorState && subscriptionCalculationRequired;
  const subscriptionCanPay = paymentType !== PaymentInfoTypeResp.abonnement
    || (!subscriptionLoadingState && !subscriptionCalculationInProgress && !subscriptionCalculationFailed);
  const isPromoAppliedSuccessfully = !!appliedPromoCodeState
    && promoInputState.trim() === appliedPromoCodeState
    && !calculationLoadingState
    && calculationState?.promoCodeInf?.code === 200
    && (calculationState?.promoAppliedQuantity ?? 0) > 0;
  const promoCodeForPayment = isPromoAppliedSuccessfully ? appliedPromoCodeState : '';
  if (paymentType === PaymentInfoTypeResp.session) {
    const params = new URLSearchParams();
    if (grzParam) params.append('grz', grzParam);
    link = doPay ? '' : `/${zone}/session/${sessionID}/payment-card${params.toString() ? `?${params.toString()}` : ''}`;
  } else if (paymentType === PaymentInfoTypeResp.abonnement) {
    const params = new URLSearchParams({
      grz: grzParam ?? '',
      phone: phoneParam ?? 'Не указан',
      timestart: requestTimestart,
    });
    if (subscriptionGrzList.length > 0) {
      params.append('subscriptionGrz', subscriptionGrzList.join(','));
    }
    if (isSubscriptionPromoEnabled) {
      params.append('timeend', calculationState?.timeend ?? timeendRawState);
      params.append('quantity', String(quantityState));
      params.append('promoCode', promoCodeForPayment);
      params.append('amount', String(calculationState?.amount ?? costState));
      params.append('promoAppliedQuantity', String(calculationState?.promoAppliedQuantity ?? 0));
    }
    link = doPay || !subscriptionCanPay ? '' : `/${zone}/subscription/${sessionID}/payment-card?${params.toString()}`;
  }

  const handleTimestartChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (!value) {
      setCustomTimestartState(timestartRawState);
      setTimeStartState(timestartRawState ? formatDateString(timestartRawState) : '--:--');
      setTimeEndState(
        durationMsState > 0
          ? formatDateString(new Date(Date.parse(timestartRawState || '') + durationMsState).toISOString())
          : (timeendRawState ? formatDateString(timeendRawState) : '--:--')
      );
      return;
    }
    const normalized = value.length === 16 ? `${value}:00` : value;
    setCustomTimestartState(normalized);
    setTimeStartState(formatDateString(normalized));
    if (durationMsState > 0) {
      const startMs = Date.parse(normalized);
      if (!isNaN(startMs)) {
        setTimeEndState(formatDateString(new Date(startMs + durationMsState).toISOString()));
      }
    }
  };

  const formatDateString = (isoString: string) => {
    const normalized = isoString.replace('T', ' ').substring(0, 16);
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime()) || !/[zZ]|[+-]\d{2}:\d{2}$/.test(isoString)) {
      const [datePart = '', timePart = ''] = normalized.split(' ');
      const [year, month, day] = datePart.split('-');
      return year && month && day ? `${day}.${month}.${year} ${timePart}`.trim() : normalized;
    }
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  const detailsIntervals = useMemo(() => {
    return (intervalsState || []).map((interval) => {
      const matches = interval.description.match(/\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2}\s*-\s*\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2}/g);
      const lines = (matches && matches.length > 0)
        ? matches.map((line) => line.trim())
        : interval.description.split(/\s{3,}/).map((line) => line.trim()).filter(Boolean);
      return {
        ...interval,
        lines,
      };
    });
  }, [intervalsState]);

  const hasIntervals = detailsIntervals.length > 0;
  const showSubscriptionDiscount = paymentType === PaymentInfoTypeResp.abonnement
    && !!calculationState
    && calculationState.discountAmount > 0;
  const promoErrorMessage = calculationErrorState
    || (appliedPromoCodeState && calculationState?.promoCodeInf?.code !== 200 ? calculationState?.promoCodeInf?.msg : '');
  const formatSubscriptionRub = (value: number | undefined) => {
    if (typeof value !== 'number') return '-';
    return Number.isInteger(value / 100) ? String(value / 100) : (value / 100).toFixed(2).replace('.', ',');
  };
  const displayCost = paymentType === PaymentInfoTypeResp.abonnement && calculationState
    ? formatSubscriptionRub(calculationState.amount)
    : (paymentType === PaymentInfoTypeResp.abonnement && isSubscriptionPromoEnabled && costState >= 0
      ? formatSubscriptionRub(costState)
      : (costState >= 0 ? String(costState) : '-'));
  const showPriceLoader = paymentType === PaymentInfoTypeResp.abonnement
    && isSubscriptionPromoEnabled
    && !calculationState
    && costState < 0
    && (subscriptionLoadingState || calculationLoadingState);

  const scrollToDetails = () => {
    detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setDetailsHighlightState(false);
    window.setTimeout(() => {
      setDetailsHighlightState(true);
      window.setTimeout(() => setDetailsHighlightState(false), 700);
    }, 120);
  }

  return (
    <Layout infoListener={handleInfoLoaded}>
      <SectionContainer fixHeight>
        <Title text="Оплатите парковку" center />
        <div className={styles.check}>
          <div className={styles.check_start}>
            <PaymentCheckSvg />
          </div>
          <div className={styles.check__price}>
            <div className={styles.check__price_title_row}>
              <div className={styles.check__price_title}>Всего к оплате:</div>
              {hasIntervals ? (
                <button type="button" className={styles.check__price_details_btn} onClick={scrollToDetails}>
                  <IcInfoSvg />
                  <span>Подробнее</span>
                </button>
              ) : null}
            </div>
            <div className={styles.check__price_price}>
              {showPriceLoader ? <span className={styles.check__price_spinner} /> : `${displayCost} ₽`}
            </div>
            <div className={styles.hidden}>
              {paymentType === PaymentInfoTypeResp.session ? (
                <div className={styles.check__price_subscribe}>
                  <Row center>
                    <div className={styles.check__price_subscribe_text}>
                      С абонементом {costState >= 0 ? costState * 0.75 : '-'} ₽
                    </div>
                    <NavLink to="subscription" className={styles.check__price_subscribe_btn}>
                      Купить
                    </NavLink>
                  </Row>
                </div>
              ) : null}
            </div>
          </div>

          {paymentType === PaymentInfoTypeResp.abonnement ? (
            subscriptionLoadingState ? (
              <div className={`${styles.check_info} ${styles.check_info_subscription_dates}`}>
                <LoaderCircle />
              </div>
            ) : isSubscriptionPromoEnabled ? (
              <div className={`${styles.check_info} ${styles.subscription_summary}`}>
                <div className={styles.subscription_summary_title}>Проверьте данные абонемента</div>
                <div className={styles.subscription_summary_period}>
                  <div>
                    <span>Начало</span>
                    <b>{timeStartState}</b>
                  </div>
                  <div>
                    <span>Окончание</span>
                    <b>{timeEndState}</b>
                  </div>
                </div>
                {subscriptionGrzList.length > 0 ? (
                  <div className={styles.subscription_summary_row}>
                    <span>Автомобили</span>
                    <div className={styles.subscription_summary_grz}>
                      {subscriptionGrzList.map((grz) => <strong key={grz}>{grz}</strong>)}
                    </div>
                  </div>
                ) : null}
                {isSubscriptionPromoEnabled && appliedPromoCodeState ? (
                  <div className={styles.subscription_summary_row}>
                    <span>Промокод</span>
                    <div className={styles.subscription_summary_line}>
                      <strong>{appliedPromoCodeState}</strong>
                      {isPromoAppliedSuccessfully && showSubscriptionDiscount ? <b>-{formatSubscriptionRub(calculationState?.discountAmount)} ₽</b> : null}
                      {promoErrorMessage ? <em>не применен</em> : null}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : !isFixedSubscriptionDates ? (
              <div className={styles.check_info}>
                <div className={styles.check_info_subscription_item}>
                  <div className={styles.check_info_subscription_item_title}>Выберите дату начала абонемента:</div>
                  <div className={styles.check_info_subscription_item_time}>
                    <input
                      type="datetime-local"
                      value={customTimestartState ? customTimestartState.slice(0, 16) : ''}
                      onChange={handleTimestartChange}
                      className={styles.date_input}
                    />
                  </div>
                </div>
                <div className={styles.check_info_subscription_item}>
                  <div className={styles.check_info_subscription_item_title}>Дата окончания абонемента:</div>
                  <div className={styles.check_info_subscription_item_time}>{timeEndState}</div>
                </div>
              </div>
            ) : null
          ) : paymentType === PaymentInfoTypeResp.session ? (
            <div className={`${styles.check_info} ${styles.check_info_subscription_promo}`}>
              <div className={styles.check_info_item}>
                <div className={styles.check_info_item_title}>Начало парковки:</div>
                <div className={styles.check_info_item_time}>{timeStartState ? timeStartState : 0}</div>
              </div>
              <div className={styles.check_info_item}>
                <div className={styles.check_info_item_title}>Конец парковки:</div>
                <div className={styles.check_info_item_time}>{timeEndState ? timeEndState : 0}</div>
              </div>
              <div className={styles.check_info_item}>
                <div className={styles.check_info_item_title}>Время парковки:</div>
                <div className={styles.check_info_item_time}>
                  {diffMinutesState < 0 ? '-' : (
                    <>
                      {diffMinutesState < 60 ? diffMinutesState : Math.floor(diffMinutesState / 60)}<span> {diffMinutesState < 60 ? 'м' : 'ч'}</span>
                    </>
                  )}
                </div>
              </div>
              {costState === 0 &&
                <div className={styles.check_info_item}>
                  <div className={styles.check_info_item_title}>
                    <b>
                      {costReasonState.trim()
                        ? `Оплата не требуется. Причина: ${costReasonState}`
                        : 'Оплата не требуется.'}
                    </b>
                  </div>
                </div>
              }
              {specialMessageState.trim() ? (
                <div className={`${styles.check_info_item} ${styles.check_info_special_message}`}>
                  <div className={styles.check_info_item_title}>
                    {specialMessageState}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

{costState !== 0 ?
  <div className={styles.check_info} translate='no'>
    <NavLink
      className={`${styles.check_info_btn} ${doPay || !subscriptionCanPay ? styles.btn_notactive : ""}`}
      to={link}
      onClick={(event) => {
        if (doPay || !subscriptionCanPay) event.preventDefault();
      }}
    >
      {subscriptionCalculationInProgress ? (
        <>
          <span className={styles.check_info_btn_spinner} />
          <span>Считаем</span>
        </>
      ) : 'Оплатить'}
    </NavLink>
  </div>
:
  <div className={styles.check_info} translate='no'>
    <NavLink className={`${styles.check_info_btn} ${doPay ? styles.btn_notactive : ""}`} to={`/${zone}/`}>Ok</NavLink>
  </div>
}
          {hasIntervals ? (
            <div className={`${styles.check_details} ${detailsHighlightState ? styles.check_details_highlight : ''}`} ref={detailsRef}>
              <div className={styles.check_details_title}>Детализация</div>
              <div className={styles.check_details_items}>
                {detailsIntervals.map((interval, idx) => (
                  <div className={styles.check_details_item} key={`${interval.name}-${idx}`}>
                    <div className={styles.check_details_item_head}>
                      <span className={styles.check_details_item_name}>{interval.name}</span>
                      <span className={styles.check_details_item_cost}>{interval.cost} ₽</span>
                    </div>
                    <div className={styles.check_details_item_lines}>
                      {(interval.lines || []).map((line, lineIdx) => (
                        <div key={`${interval.name}-${idx}-${lineIdx}`}>{line}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

        </div>
        <div className={styles.check_end}>
          <EndCheckSvg />
      
      
        </div>

      </SectionContainer>
    </Layout>
  )
}

export default PaymentPage;

